/**
 * Email API service for frontend communication with backend email endpoints
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

/**
 * Handle API response and errors
 * @param {Response} response - Fetch response object
 * @returns {Promise<Object>} Parsed response data
 */
const handleResponse = async (response) => {
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || `HTTP error! status: ${response.status}`);
  }
  
  return data;
};

/**
 * Initialize email service on backend
 * @returns {Promise<Object>} Result object with success status
 */
export const initializeEmailService = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/email/initialize`, {
      method: 'POST',
      headers: JSON_HEADERS,
      credentials: 'include',
    });
    
    return await handleResponse(response);
  } catch (error) {
    console.error('Initialize email service error:', error);
    return {
      success: false,
      message: error.message || 'Failed to initialize email service'
    };
  }
};

/**
 * Get email service status
 * @returns {Promise<Object>} Status object
 */
export const getEmailStatus = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/email/status`, {
      method: 'GET',
      headers: JSON_HEADERS,
      credentials: 'include',
    });
    
    return await handleResponse(response);
  } catch (error) {
    console.error('Get email status error:', error);
    return {
      success: false,
      message: error.message || 'Failed to get email status'
    };
  }
};

/**
 * Send client contact email
 * @param {Object} contactData - Contact email data
 * @param {string} contactData.contactEmail - Recipient email
 * @param {string} contactData.contactName - Recipient name
 * @param {string} contactData.clientName - Client name
 * @param {string} contactData.subject - Email subject
 * @param {string} contactData.message - Email message
 * @param {string} contactData.senderName - Sender name (optional)
 * @returns {Promise<Object>} Result object with success status
 */
export const sendClientContactEmail = async (contactData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/email/send-contact`, {
      method: 'POST',
      headers: JSON_HEADERS,
      credentials: 'include',
      body: JSON.stringify(contactData)
    });
    
    return await handleResponse(response);
  } catch (error) {
    console.error('Send client contact email error:', error);
    return {
      success: false,
      message: error.message || 'Failed to send email'
    };
  }
};

/**
 * Send export email with Excel attachment
 * @param {Object} exportData - Export email data
 * @param {string} exportData.email - Recipient email
 * @param {Array} exportData.clients - Array of client data
 * @param {boolean} exportData.includeRevenue - Include revenue information
 * @param {boolean} exportData.includeContacts - Include contact details
 * @param {boolean} exportData.includeNotes - Include client notes
 * @param {string} exportData.format - File format (xlsx or csv)
 * @returns {Promise<Object>} Result object with success status
 */
export const sendExportEmail = async (exportData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/email/send-export`, {
      method: 'POST',
      headers: JSON_HEADERS,
      credentials: 'include',
      body: JSON.stringify(exportData)
    });
    
    return await handleResponse(response);
  } catch (error) {
    console.error('Send export email error:', error);
    return {
      success: false,
      message: error.message || 'Failed to send export email'
    };
  }
};

/**
 * Send test email
 * @param {string} email - Test email address
 * @returns {Promise<Object>} Result object with success status
 */
export const sendTestEmail = async (email) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/email/send-test`, {
      method: 'POST',
      headers: JSON_HEADERS,
      credentials: 'include',
      body: JSON.stringify({ email })
    });
    
    return await handleResponse(response);
  } catch (error) {
    console.error('Send test email error:', error);
    return {
      success: false,
      message: error.message || 'Failed to send test email'
    };
  }
};

/**
 * Check if email service is configured and ready
 * @returns {Promise<boolean>} True if email service is ready
 */
export const isEmailServiceReady = async () => {
  try {
    const statusResult = await getEmailStatus();
    return statusResult.success && statusResult.status && statusResult.status.ready;
  } catch (error) {
    console.error('Check email service ready error:', error);
    return false;
  }
};

/**
 * Validate email address format
 * @param {string} email - Email address to validate
 * @returns {boolean} True if email format is valid
 */
export const validateEmailFormat = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Check if email appears to be auto-generated
 * @param {string} email - Email address to check
 * @returns {boolean} True if email appears to be auto-generated
 */
export const isAutoGeneratedEmail = (email) => {
  if (!email) return true;
  
  const autoGeneratedPatterns = [
    'contact@',
    'info@',
    'noreply@',
    'no-reply@',
    'admin@',
    'support@',
    'example.com',
    'test.com',
    'placeholder'
  ];
  
  const lowerEmail = email.toLowerCase();
  return autoGeneratedPatterns.some(pattern => lowerEmail.includes(pattern));
};

/**
 * Get email service configuration status message
 * @returns {Promise<string>} Status message for UI display
 */
export const getEmailConfigurationMessage = async () => {
  try {
    const statusResult = await getEmailStatus();
    
    if (!statusResult.success) {
      return 'Email service unavailable';
    }
    
    if (!statusResult.status.configured) {
      return 'Email service not configured';
    }
    
    if (!statusResult.status.ready) {
      return 'Email service not ready';
    }
    
    return 'Email service ready';
  } catch (error) {
    return 'Email service error';
  }
};

// Default export with all functions
const emailApi = {
  initializeEmailService,
  getEmailStatus,
  sendClientContactEmail,
  sendExportEmail,
  sendTestEmail,
  isEmailServiceReady,
  validateEmailFormat,
  isAutoGeneratedEmail,
  getEmailConfigurationMessage
};

export default emailApi;
